import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { BarChart3, ArrowLeft, Loader2, CheckCircle2, Circle } from "lucide-react";
import { fmtDate } from "../lib/progressFormat";

function Step({ label, at, testid }) {
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={testid}>
      {at ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <Circle className="w-4 h-4 text-muted-foreground/50 shrink-0" />}
      <span className="font-semibold">{label}</span>
      <span className="text-muted-foreground">{at ? fmtDate(at) : "Not yet"}</span>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function WorksheetList({ items }) {
  if (!items.length) return null;
  return (
    <ul className="mt-3 space-y-1.5">
      {items.map((w) => (
        <li key={w.worksheet_id} className="flex flex-wrap items-center justify-between gap-2 text-xs" data-testid={`worksheet-${w.worksheet_id}`}>
          <span className="text-foreground/80">{w.title}</span>
          <span className={w.status === "submitted" ? "text-emerald-700 font-semibold" : "text-muted-foreground"}>
            {w.status === "submitted" ? `Submitted ${fmtDate(w.submitted_at)}`
              : w.status === "in_progress" ? `In progress · ${Math.round(w.completion_pct)}%` : "Not started"}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ModuleCard({ m }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid={`module-progress-${m.slug}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold">{m.name}</h3>
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{m.track}</span>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <Step label="Video watched" at={m.video_watched_at} testid={`step-video-${m.slug}`} />
        <Step label="Reading reviewed" at={m.reading_read_at} testid={`step-reading-${m.slug}`} />
        <Step label="Quiz passed" at={m.quiz_passed_at} testid={`step-quiz-${m.slug}`} />
      </div>
      <p className="mt-2 text-xs text-muted-foreground" data-testid={`quiz-stats-${m.slug}`}>
        Quiz: best score {m.quiz_best_score ?? "—"}{m.quiz_best_score != null ? "%" : ""} · {m.quiz_attempts} attempt{m.quiz_attempts === 1 ? "" : "s"}
      </p>
      <WorksheetList items={m.worksheets} />
    </div>
  );
}

function ExamAndCerts({ exam, certs }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="final-exam-progress">
        <h3 className="font-semibold">Final exam</h3>
        <div className="mt-3"><Step label="Passed" at={exam.passed_at} testid="step-final-exam" /></div>
        <p className="mt-2 text-xs text-muted-foreground">
          Best score {exam.best_score ?? "—"}{exam.best_score != null ? "%" : ""} · {exam.attempts} attempt{exam.attempts === 1 ? "" : "s"}
        </p>
      </div>
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="certificates-progress">
        <h3 className="font-semibold">Certificates earned ({certs.length})</h3>
        <ul className="mt-3 space-y-1.5">
          {certs.length === 0 && <li className="text-xs text-muted-foreground">None yet</li>}
          {certs.map((c) => (
            <li key={c.credential_id} className="flex flex-wrap justify-between gap-2 text-xs" data-testid={`cert-${c.credential_id}`}>
              <span className="font-semibold">{c.cert_name}</span>
              <span className="text-muted-foreground">{fmtDate(c.issued_at)} · {c.credential_id}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function AdminLearnerProgressDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/admin/progress/users/${userId}`).then((r) => setData(r.data)).catch((e) => setError(formatError(e)));
  }, [userId]);

  const s = data?.summary;
  return (
    <AppShell>
      <Link to="/admin/progress" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        data-testid="learner-progress-back"><ArrowLeft className="w-4 h-4" /> All learners</Link>
      <PageHero eyebrow="Learner Progress" icon={BarChart3}
        title={data ? (data.user.name || data.user.email) : "Loading…"}
        subtitle={data ? `${data.user.email} · ${data.user.package_slug || "no plan"}` : ""}
        testid="learner-detail-hero" />
      {error && <p className="text-sm text-destructive" data-testid="learner-detail-error">{error}</p>}
      {!data && !error && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
      {data && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="% Complete" value={`${s.percent_complete}%`} testid="stat-percent" />
            <Stat label="Videos watched" value={`${s.videos_watched}/${s.modules_total}`} testid="stat-videos" />
            <Stat label="Readings reviewed" value={`${s.readings_reviewed}/${s.modules_total}`} testid="stat-readings" />
            <Stat label="Quizzes passed" value={`${s.quizzes_passed}/${s.modules_total}`} testid="stat-quizzes" />
            <Stat label="Worksheets" value={`${s.worksheets_submitted}/${s.worksheets_total}`} testid="stat-worksheets" />
          </div>
          <ExamAndCerts exam={data.final_exam} certs={data.certificates} />
          <div className="space-y-4">{data.modules.map((m) => <ModuleCard key={m.slug} m={m} />)}</div>
        </div>
      )}
    </AppShell>
  );
}
